import { styled } from '@mui/material/styles';
import { Box, Button, TableCell, TableContainer, Typography } from '@mui/material';
import CommonText from '../../../form-components/CommonText';

// ============================================
// Layout
// ============================================
export const PageContainer = styled('div')({
  background: 'white',
  borderRadius: '5px',
  width: '100%',
});

// ============================================
// Profile / Avatar
// ============================================
export const AvatarImage = styled('img')({
  width: '135px',
  height: '135px',
  objectFit: 'cover',
  borderRadius: '50%',
});

// "상세보기" / "보호자등록" 같은 작은 흰바탕 + teal border 버튼
export const OutlinedRoundButton = styled(Button)({
  width: '100px',
  height: '27px',
  borderRadius: '15px',
  fontSize: '12px',
  fontWeight: 'bold',
  background: '#FFFFFF',
  color: '#4AB3BC',
  border: '1px solid #4AB3BC',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#4AB3BC',
    color: '#FFFFFF',
  },
});

// ============================================
// Section / Subsection Titles
// ============================================
// "Studio" / "Registration date" / "Management" / "Remarks" 같은 폼 라벨
// CommonText의 label variant(16/600/#262626)을 베이스로 layout만 추가.
export const SectionTitle = styled(CommonText)({
  width: '135px',
  marginBottom: '8px',
});

// "이용 정보" 같은 큰 섹션 헤더 (18/500/black) — CommonText에 깔끔히 매칭되는 variant 없어 직접 정의.
export const SectionHeader = styled(Typography)({
  fontSize: '18px',
  fontWeight: 500,
});

// "잔여 활동 정보" / "최근 수업 예약 내역" 같은 sub 라벨 — caption variant + 마진.
export const SubsectionLabel = styled(CommonText)({
  marginBottom: '8px',
});

// "자세히 보기" 링크 텍스트
export const MoreLink = styled(Typography)({
  cursor: 'default',
  textDecoration: 'underline',
  color: '#a1a1a1',
  fontSize: '14px',
  fontWeight: 400,
  '&:hover': {
    color: '#0F7F85',
  },
});

// ============================================
// Profile / Memo Sections (회원 상세 첫 탭 상단)
// 아바타 + 기본 정보 vs 메모를 시각적으로 분리하는 카드형 섹션.
// ============================================
export const ProfileSectionRow = styled(Box)({
  display: 'flex',
  gap: '20px',
  width: '100%',
});

// 상단 이름 카드(UserInfoCard, flex 0 0 30%)와 가로 폭을 맞춰 좌측 정렬 대칭을 유지한다.
export const ProfileSection = styled(Box)({
  flex: '0 0 30%',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '20px 24px',
  border: '1px solid #ebebeb',
  borderRadius: '8px',
  background: '#FAFBFC',
});

// 프로필 우측 — 상담 내역 요약 테이블 자리. (프로필 30% 제외 나머지 폭 차지)
export const ConsultationSection = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 24px',
  border: '1px solid #ebebeb',
  borderRadius: '8px',
  background: '#FAFBFC',
});

// ============================================
// 보유 포인트 (프로필 하단)
// ============================================
export const PointRow = styled(Box)({
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '10px 14px',
  background: '#fff',
  border: '1px solid #4AB3BC',
  borderRadius: '6px',
});

export const PointLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#4B5563',
});

export const PointValue = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#0F7F85',
});

export const PointChangeButton = styled(Button)({
  minWidth: 0,
  height: '24px',
  padding: '0 10px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#fff',
  background: '#4AB3BC',
  '&:hover': {
    background: '#0F7F85',
  },
});

// ============================================
// Stat Cards (잔여 활동 정보 / 센터 이용 정보)
// 통계 한 행을 카드들의 가로 그리드로 표시. 각 카드 = 라벨 + 값.
// ============================================
export const StatCardGrid = styled(Box)({
  display: 'flex',
  gap: '12px',
  width: '100%',
  marginTop: '12px',
  marginBottom: '8px',
});

// tone='danger': 만료됐거나 임박한 값 — 옅은 red 배경 + red 값 (디자인 리뉴얼)
export const StatCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  flex: 1,
  minWidth: 0,
  background: tone === 'danger' ? '#FFF1F1' : '#fff',
  border: `1px solid ${tone === 'danger' ? '#FADBDB' : '#ebebeb'}`,
  borderRadius: '8px',
  padding: '16px 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: '8px',
}));

export const StatLabel = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#737373',
});

export const StatValue = styled(Typography, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: tone === 'danger' ? '#E5484D' : '#262626',
}));

// ============================================
// Data Tables (최근 수업 예약 내역 / 사용 중인 수강권)
// ============================================
export const DataTableContainer = styled(TableContainer)({
  border: '1px solid #ebebeb',
  borderRadius: '4px',
  width: '100%',
});

export const DataTableHeadCell = styled(TableCell)({
  borderRight: '1px solid #ebebeb',
  fontSize: '14px',
  fontWeight: 550,
});

export const DataTableBodyCell = styled(TableCell)({
  borderRight: '1px solid #ebebeb',
  fontSize: '14px',
});

// ============================================
// Bottom Action Buttons
// ============================================
export const BottomActionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

// 취소/저장/삭제 버튼은 form-components/CommonFormButton 사용.
// 모달 레이아웃은 form-components/CommonModalLayout 사용.
